/**
 * Shared helpers for extracting employee mentions from HTML produced by the
 * `<at>` picker (vue-at). Used by Tasks (TodoDetails.vue, ToDo.vue) and Kanban
 * (Kanban.vue Add modal, TaskCard.vue Edit modal) to identify @-mentioned
 * employees for notification writes.
 *
 * The <at> picker embeds selections as:
 *   <span data-teamio-employeeid="abc-123" ...>Priya</span>
 *
 * These helpers only read the embedded IDs — they don't parse plain-text names.
 * If a user types "@Priya" without selecting from the picker, no ID is embedded
 * and no notification fires. That's intentional: the picker is the single
 * source of truth for who got mentioned.
 *
 * Unit tests: cypress/node-tests/task-mentions.test.js
 */

/**
 * Extract ALL mentioned employee IDs from a contenteditable HTML string.
 * Returns IDs in order of appearance; duplicates are preserved (callers dedupe
 * if they need to).
 *
 * @param {string} contentHTML
 * @returns {string[]} employee IDs (may contain duplicates)
 */
export function getAllLinkedEmployeeIDs(contentHTML) {
  if (!contentHTML || typeof contentHTML !== 'string') return [];
  const regex = /data-teamio-employeeid="([a-zA-Z0-9-]+)/g;
  const ids = [];
  let match;
  while ((match = regex.exec(contentHTML)) !== null) {
    ids.push(match[1]);
  }
  return ids;
}

/**
 * Same as getAllLinkedEmployeeIDs but deduplicates — returns each ID once.
 * Useful when computing the assignee set for a card.
 *
 * @param {string} contentHTML
 * @returns {string[]} unique employee IDs, in order of first appearance
 */
export function getUniqueLinkedEmployeeIDs(contentHTML) {
  const seen = new Set();
  const result = [];
  for (const id of getAllLinkedEmployeeIDs(contentHTML)) {
    if (!seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  return result;
}

/**
 * Given two contentHTML strings (original + new), return the employee IDs that
 * appear in the new version but NOT in the original. Used by edit flows to fire
 * notifications ONLY for newly-added mentions, not pre-existing ones.
 *
 * @param {string} originalHTML
 * @param {string} newHTML
 * @returns {string[]} newly-added employee IDs
 */
export function getNewlyMentionedEmployeeIDs(originalHTML, newHTML) {
  const original = new Set(getUniqueLinkedEmployeeIDs(originalHTML));
  const now = getUniqueLinkedEmployeeIDs(newHTML);
  return now.filter(id => !original.has(id));
}
